/**
 * `keyCommands` (W1-10b core-commands-motion; DESIGN.md §4.7, §5.6, §6; rpe-editor-features.md E§3.8, E§3.9,
 * E§4.4-4.8, E§4.14). Every key / segment / layer business rule of the motion columns and the curve editor:
 *
 *  - insert (shape-preserving split, splitKey.ts), insert on several properties, values (continuity rule and the
 *    hold invariant), expressions, move (clamped, order kept), delete (segments merge keeping the earlier curve;
 *    the first key cannot be deleted while others remain), easing (EaseUse guards; skipped count), Stay toggle,
 *    jumps (make / remove front to back, MULTI-03), RPE linear split, bake (RPE Cut grid anchoring), join (exact
 *    cases only), extrapolate (RPE Pass), reverse (exact), link groups, layers (layers.ts), raw track replace.
 *  - Link groups: value / curve edits are written to every member of the group (link.ts `syncLinkGroups`).
 *  - Position X/Y link: insert, move, delete, split, bake, join, reverse and easing changes on one axis are
 *    mirrored on the other axis at the same beat (link.ts `withXyPartners`); moving segments to another layer
 *    moves the other axis over the same spans and holding an imported lead-in gives the other axis its beat-0 key
 *    too (layers.ts), removing time cuts both axes alike (transform.ts), and paste aligns both axes' beats inside
 *    the pasted spans (link.ts `alignXyBeats`). An absent other axis that an insertion (insert, Shift+K, RPE
 *    split) creates gets a key of 0 at every beat of the edited axis (exact; flagged as an imported lead-in next to
 *    one). When the other axis exists, a created axis (ensureTrack, insertKeysAt) and a raw event replacement align
 *    both axes over all their keys (link.ts `alignXyAll`), and a reverse that split at tempo changes aligns them
 *    inside its span (CR-W1-10b-14).
 *  - Imported lead-ins (the first segment continued backwards before a first key after beat 0): bake and reverse
 *    of the first segment make that lead-in an explicit segment from beat 0 first (motionTime.ts
 *    `explicitLeadIn`), so the curve before the operated segments is kept (exact for a straight first segment,
 *    otherwise noted).
 *  - Commands that write a segment's end value (extrapolate, link sync, add-values paste) keep a last key without a
 *    jump continuous (motionTrack.ts `setArrive`): the curve holds the new end after it, as RPE and Phira do.
 *  - Approximate shape-preserving splits are noted once per command (motionNotes.ts).
 *  - Every command writes tracks through `commitKeys` (motionTrack.ts), which keeps the hold, first-key, GIF and
 *    lead-in invariants.
 */

import { Ease, HOLD, LINEAR } from '../ease/index';
import type { Ease as EaseValue } from '../ease/types';
import { evaluateProgram, Expr } from '../expr/index';
import type { ExprError } from '../expr/types';
import type { LiftedKey } from '../ir/types';
import { isUiMover, kindOfLine } from '../model/lineKind';
import { LINE_PROPS, PROP_SPECS, propAppliesTo } from '../model/props';
import type { KeyId, LineProp, TrackValue } from '../model/types';
import type { TxBuilder } from '../ops/types';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import type { KeyCommands, KeyValuePart } from './types';
import {
  addLayerBody,
  deleteLayerBody,
  ensureTrackBody,
  holdLeadInBody,
  moveSegmentsToLayerBody,
  resetPropertyBody,
} from './layers';
import {
  alignXyAll,
  alignXyWindows,
  cleanLinks,
  keyAtBeat,
  linkedNeighbours,
  nextLinkId,
  startsSegment,
  syncLinkGroups,
  withXyPartners,
  xyPartnerOf,
  xyPartnerTrack,
  noteXyWindow,
  type XyWindow,
} from './link';
import {
  commitKeys,
  earliestBeat,
  easeUseFor,
  enforceKeyRules,
  groupByTrack,
  indexAt,
  indexOfId,
  isValueOf,
  MOTION_MSG,
  neutralValue,
  readKeys,
  reason,
  requireLine,
  requireTrack,
  requireValue,
  sameValue,
  setArrive,
  tempoChangeBeats,
  tidy,
  trackInfo,
  type KeyRec,
  type TrackInfo,
} from './motionTrack';
import { explicitLeadIn, reverseWindow } from './motionTime';
import {
  bakeSegmentAt,
  curveCtx,
  easeY,
  insertKeyRec,
  interp,
  rpeLinearSplit,
  setBoth,
  storeValue,
  valueAtBeat,
  worst,
  type CurveCtx,
  type RawValue,
  type SplitReport,
} from './splitKey';
import { collectSplit, noteSplit, notePartnerSplit, noteWorstSplit, worseSplitOn, type SplitOn } from './motionNotes';

// ---------------------------------------------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------------------------------------------

interface Inserted extends SplitReport {
  readonly key: KeyId;
}

/** An insertion and the track it happened on. */
type InsertedOn = SplitOn<Inserted>;

function focusOn(tx: TxBuilder, info: TrackInfo | null, beat: Beat | null): void {
  const line = info?.line?.id;
  if (beat === null) {
    if (line !== undefined) tx.setFocus({ line });
    return;
  }
  tx.setFocus(line === undefined ? { beat } : { beat, line });
}

/** Shape-preserving insert on one track (writes it). */
function insertOn(
  tx: TxBuilder,
  info: TrackInfo,
  t: Beat,
  value: TrackValue | undefined,
  unlinked: Set<number>,
): Inserted {
  const keys = readKeys(tx, info.track.id);
  const out = insertKeyRec(keys, t, curveCtx(tx, info), unlinked, value);
  commitKeys(tx, info, keys);
  const key = (keys[indexAt(keys, t)] as KeyRec).id as KeyId;
  if (!out.created && value !== undefined) syncLinkGroups(tx, linkedNeighbours(tx, key));
  return {
    key,
    exactness: out.exactness,
    maxRelError: out.maxRelError,
    ...(out.approx === undefined ? {} : { approx: out.approx }),
    ...(out.leadInRelError === undefined ? {} : { leadInRelError: out.leadInRelError }),
    ...(out.textShiftSec === undefined ? {} : { textShiftSec: out.textShiftSec }),
  };
}

/**
 * Mirrors a key insertion onto the other position axis. An absent other axis is created as a constant 0 with a
 * key at every beat of the edited axis (link.ts `alignXyAll`; exact: an absent axis is 0), so both axes share all
 * their beats. Returns the partner's insertion, or null when there is no partner axis.
 */
function mirrorInsert(tx: TxBuilder, info: TrackInfo, t: Beat, unlinked: Set<number>): InsertedOn | null {
  const partner = xyPartnerOf(info);
  if (partner === null || info.prop === null) return null;
  const id = xyPartnerTrack(tx, info);
  if (id === undefined) {
    alignXyAll(tx, partner.line, partner.layer, [info.prop], unlinked);
    const created = xyPartnerTrack(tx, info);
    const cInfo = created === undefined ? null : trackInfo(tx, created);
    const key = created === undefined ? undefined : keyAtBeat(tx, created, t);
    return cInfo === null || key === undefined
      ? null
      : { info: cInfo, res: { key, exactness: 'exact', maxRelError: 0 } };
  }
  const pInfo = trackInfo(tx, id);
  return pInfo === null ? null : { info: pInfo, res: insertOn(tx, pInfo, t, undefined, unlinked) };
}

/**
 * After a position axis was created on an X/Y-linked line whose other axis exists: both axes get a key at every
 * beat either one has (link.ts `alignXyAll`; the new axis' splits are exact, the other axis' are shape-preserving).
 * Returns the worst split (the other axis' on its own track), or null.
 */
function alignCreatedAxis(tx: TxBuilder, info: TrackInfo, unlinked: Set<number>): SplitOn | null {
  const partnerId = xyPartnerTrack(tx, info);
  if (partnerId === undefined || info.line === null || info.prop === null) return null;
  const aligned = alignXyAll(tx, info.line.id, info.layer, [info.prop], unlinked);
  const pInfo = trackInfo(tx, partnerId);
  return pInfo === null ? aligned.edited : worseSplitOn(aligned.edited, collectSplit(null, pInfo, aligned.partner));
}

/** Selected key indices of a track's records, ascending. */
function selectedIndices(keys: readonly KeyRec[], ids: readonly KeyId[]): number[] {
  const set = new Set(ids);
  const out: number[] = [];
  keys.forEach((k, i) => {
    if (k.id !== null && set.has(k.id)) out.push(i);
  });
  return out;
}

/** Applies a value edit to key i of a track (see `setKeyValues`); false when the edit was refused (after a Stay). */
function applyValue(keys: KeyRec[], i: number, part: KeyValuePart, v: TrackValue, info: TrackInfo): boolean {
  const k = keys[i];
  if (k === undefined) return false;
  const prev = keys[i - 1];
  const afterHold = prev !== undefined && prev.ease.kind === 'hold';
  const lastGif = info.prop === 'gif' && i === keys.length - 1;
  if (i === 0) {
    k.vIn = v;
    k.vOut = v;
  } else if (lastGif) {
    // The last GIF key cannot jump; after a Stay its arrive value (and so the key) is the stayed value.
    if (afterHold) return false;
    k.vIn = v;
    k.vOut = v;
  } else if (part === 'both') {
    setBoth(keys, i, v);
  } else if (part === 'in') {
    if (afterHold) return false;
    k.vIn = v;
  } else {
    k.vOut = v;
  }
  const next = keys[i + 1];
  if (k.ease.kind === 'hold' && next !== undefined) next.vIn = k.vOut;
  return true;
}

// ---------------------------------------------------------------------------------------------------------------
// Insert
// ---------------------------------------------------------------------------------------------------------------

const insertKey: KeyCommands['insertKey'] = (tx, { track, t, value }) => {
  const info = requireTrack(tx, track);
  const v = value === undefined ? undefined : requireValue(tx, info, value);
  const unlinked = new Set<number>();
  const res = insertOn(tx, info, t, v, unlinked);
  const partner = mirrorInsert(tx, info, t, unlinked);
  cleanLinks(tx, unlinked);
  noteSplit(tx, info, res);
  notePartnerSplit(tx, partner?.res);
  focusOn(tx, info, t);
  return { key: res.key, exactness: partner === null ? res.exactness : worst(res.exactness, partner.res.exactness) };
};

const ensureTrack: KeyCommands['ensureTrack'] = (tx, { owner, value, at }) => {
  if (tx.idx.trackOfOwner(owner) !== undefined) return ensureTrackBody(tx, owner, value, at);
  const id = ensureTrackBody(tx, owner, value, at);
  // A new position axis on an X/Y-linked line shares every beat with the other axis (both get the missing keys).
  const info = trackInfo(tx, id);
  if (info !== null && info.line !== null && info.prop !== null && xyPartnerTrack(tx, info) !== undefined) {
    const unlinked = new Set<number>();
    const aligned = alignXyAll(tx, info.line.id, info.layer, [info.prop], unlinked);
    cleanLinks(tx, unlinked);
    noteWorstSplit(tx, aligned.edited);
    notePartnerSplit(tx, aligned.partner);
  }
  return id;
};

const insertKeysAt: KeyCommands['insertKeysAt'] = (tx, { line, props, layer, t }) => {
  const rec = requireLine(tx, line);
  if (!Number.isSafeInteger(layer) || layer < 0) tx.fail(reason(MOTION_MSG.layerInvalid));
  const layerOf = (p: LineProp): number => (PROP_SPECS[p].layered ? layer : 0);
  const animated = props === 'animated';
  const list: readonly LineProp[] = animated
    ? LINE_PROPS.filter((p) => {
        const id = tx.idx.track(line, p, layerOf(p));
        return id !== undefined && tx.idx.keysByTrack(id).length >= 2;
      })
    : props;
  const unlinked = new Set<number>();
  const out: KeyId[] = [];
  let worstOne: SplitOn | null = null;
  for (const p of [...new Set(list)]) {
    const id = tx.idx.track(line, p, layerOf(p));
    if (id === undefined) {
      if (animated) continue;
      // Properties that would change what the line is (text, paint, GIF) are never created implicitly.
      if (p === 'gif' || p === 'text' || p === 'paint') continue;
      const kind = kindOfLine(tx.doc, tx.idx, rec);
      if (!propAppliesTo(p, kind)) continue;
      const created = ensureTrackBody(
        tx,
        { kind: 'line', line, prop: p, layer: layerOf(p) },
        neutralValue({ prop: p, layer: layerOf(p), kind, uiMover: isUiMover(rec), valueType: PROP_SPECS[p].valueType }),
        t,
      );
      const k = keyAtBeat(tx, created, t);
      if (k !== undefined) out.push(k);
      const cInfo = trackInfo(tx, created);
      if (cInfo === null) continue;
      // A created position axis shares every beat with the other one (an absent other axis is created as 0).
      worstOne = worseSplitOn(
        worstOne,
        xyPartnerTrack(tx, cInfo) === undefined
          ? mirrorInsert(tx, cInfo, t, unlinked)
          : alignCreatedAxis(tx, cInfo, unlinked),
      );
      continue;
    }
    const info = requireTrack(tx, id);
    const r = insertOn(tx, info, t, undefined, unlinked);
    out.push(r.key);
    worstOne = worseSplitOn(worseSplitOn(worstOne, { info, res: r }), mirrorInsert(tx, info, t, unlinked));
  }
  if (out.length === 0) tx.fail(reason(animated ? MOTION_MSG.nothingAnimated : MOTION_MSG.noTrack));
  cleanLinks(tx, unlinked);
  // One toast for the property whose curve changed the most.
  if (worstOne !== null) noteSplit(tx, worstOne.info, worstOne.res);
  tx.setFocus({ beat: t, line });
  return out;
};

// ---------------------------------------------------------------------------------------------------------------
// Values
// ---------------------------------------------------------------------------------------------------------------

/**
 * The part a one-sided edit writes so a continuous key stays continuous (UX-033; RPE autostick EVT-14, DESIGN.md
 * §6 "continuity by construction"): 'both' when the key arrives and leaves at the same value (the whole key moves;
 * after a Stay only its leave value, per the hold invariant), else `side` (an existing jump keeps its other side).
 */
function continuityPartOf(keys: readonly KeyRec[], i: number, side: KeySide): KeyValuePart {
  const k = keys[i];
  if (k === undefined) return side;
  return i === 0 || sameValue(k.vIn, k.vOut) ? 'both' : side;
}

/** One side of a key, as the inspector's From (leave) / To (arrive) fields and add-values paste edit it. */
export type KeySide = 'in' | 'out';

/** Writes per-key values; returns the number of keys whose values changed. */
function writeValues(
  tx: TxBuilder,
  edits: ReadonlyMap<KeyId, TrackValue>,
  part: KeyValuePart | { readonly side: KeySide },
): number {
  let changed = 0;
  let refused = 0;
  const touched: KeyId[] = [];
  for (const [trackId, ids] of groupByTrack(tx, [...edits.keys()])) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    const before = keys.map((k) => ({ vIn: k.vIn, vOut: k.vOut }));
    for (const i of selectedIndices(keys, ids)) {
      const k = keys[i] as KeyRec;
      const v = edits.get(k.id as KeyId);
      if (v === undefined) continue;
      const p = typeof part === 'string' ? part : continuityPartOf(keys, i, part.side);
      if (!applyValue(keys, i, p, v, info)) refused++;
    }
    enforceKeyRules(keys, info);
    keys.forEach((k, i) => {
      const b = before[i];
      if (b !== undefined && (!sameValue(b.vIn, k.vIn) || !sameValue(b.vOut, k.vOut))) {
        changed++;
        if (k.id !== null) touched.push(k.id);
      }
    });
    commitKeys(tx, info, keys);
  }
  if (changed === 0 && refused > 0) tx.fail(reason(MOTION_MSG.arriveAfterHold));
  if (refused > 0) tx.note(reason(MOTION_MSG.arriveAfterHold));
  syncLinkGroups(
    tx,
    touched.flatMap((id) => linkedNeighbours(tx, id)),
  );
  return changed;
}

const setKeyValues: KeyCommands['setKeyValues'] = (tx, { keys, part, value }) => {
  const edits = new Map<KeyId, TrackValue>();
  for (const [trackId, ids] of groupByTrack(tx, keys)) {
    const info = requireTrack(tx, trackId);
    const v = requireValue(tx, info, value);
    for (const id of ids) edits.set(id, v);
  }
  if (edits.size === 0) tx.fail(reason(MOTION_MSG.keyMissing));
  writeValues(tx, edits, part);
  focusOn(tx, null, earliestBeat(tx, edits.keys()));
};

/** Evaluates an expression over number keys (exprKeys' scope: v, i, n, u, t, s, v0, durSec); per-key results. */
function exprEdits(
  tx: TxBuilder,
  keys: readonly KeyId[],
  part: KeyValuePart,
  prog: Parameters<KeyCommands['exprKeys']>[1]['prog'],
  seed: number,
): Map<KeyId, TrackValue> {
  const items = [...new Set(keys)]
    .map((id) => tx.doc.keys.get(id))
    .filter((k): k is NonNullable<typeof k> => k !== undefined && tx.doc.tracks.get(k.track)?.valueType === 'number')
    .sort((a, b) => Beats.cmp(a.t, b.t) || a.id - b.id);
  if (items.length === 0) tx.fail(reason(MOTION_MSG.exprNoNumbers));
  const rng = Expr.rng(seed);
  const n = items.length;
  const edits = new Map<KeyId, TrackValue>();
  let failed = 0;
  let firstError: ExprError | null = null;
  for (let i = 0; i < n; i++) {
    const k = items[i] as (typeof items)[number];
    const cur = (part === 'in' ? k.vIn : k.vOut) as number;
    const prevId = tx.idx.prevKey(k.id);
    const prev = prevId === undefined ? undefined : tx.doc.keys.get(prevId);
    const s = tx.tempo.secAt(k.t);
    const scope = {
      v: cur,
      i,
      n,
      u: n > 1 ? i / (n - 1) : 0,
      t: Beats.toF64(k.t),
      s,
      v0: typeof prev?.vOut === 'number' ? prev.vOut : cur,
      durSec: prev === undefined ? 0 : s - tx.tempo.secAt(prev.t),
    };
    const r = evaluateProgram(prog, scope, rng, tx.tempo);
    if (!r.ok) {
      failed++;
      firstError ??= r.error;
      continue;
    }
    edits.set(k.id, tidy(r.value));
  }
  if (edits.size === 0) tx.fail(firstError ?? reason(MOTION_MSG.exprNoNumbers));
  if (failed > 0) tx.note(reason(MOTION_MSG.exprSkipped, { count: failed }));
  return edits;
}

const exprKeys: KeyCommands['exprKeys'] = (tx, { keys, part, prog, seed }) => {
  const edits = exprEdits(tx, keys, part, prog, seed);
  const changed = writeValues(tx, edits, part);
  focusOn(tx, null, earliestBeat(tx, edits.keys()));
  return changed;
};

/**
 * `setKeyValues` for ONE side of each key that keeps continuity (UX-033): a continuous key gets both values (the
 * whole key moves, RPE autostick), a key that already is a jump only `side`. The inspector's From / To fields,
 * add-values paste and expression edits of a side use it, so editing a segment's end never silently makes a jump.
 */
export function setKeySide(
  tx: TxBuilder,
  a: { readonly keys: readonly KeyId[]; readonly side: KeySide; readonly value: TrackValue },
): void {
  const edits = new Map<KeyId, TrackValue>();
  for (const [trackId, ids] of groupByTrack(tx, a.keys)) {
    const info = requireTrack(tx, trackId);
    const v = requireValue(tx, info, a.value);
    for (const id of ids) edits.set(id, v);
  }
  if (edits.size === 0) tx.fail(reason(MOTION_MSG.keyMissing));
  writeValues(tx, edits, { side: a.side });
  focusOn(tx, null, earliestBeat(tx, edits.keys()));
}

/** `exprKeys` for one side with the continuity rule of `setKeySide`; returns the number of changed keys. */
export function exprKeySide(
  tx: TxBuilder,
  a: {
    readonly keys: readonly KeyId[];
    readonly side: KeySide;
    readonly prog: Parameters<KeyCommands['exprKeys']>[1]['prog'];
    readonly seed: number;
  },
): number {
  const edits = exprEdits(tx, a.keys, a.side, a.prog, a.seed);
  const changed = writeValues(tx, edits, { side: a.side });
  focusOn(tx, null, earliestBeat(tx, edits.keys()));
  return changed;
}

const makeJump: KeyCommands['makeJump'] = (tx, { key, vIn, vOut }) => {
  const k = tx.doc.keys.get(key) ?? tx.fail(reason(MOTION_MSG.keyMissing));
  const info = requireTrack(tx, k.track);
  const a = requireValue(tx, info, vIn);
  const b = requireValue(tx, info, vOut);
  const keys = readKeys(tx, k.track);
  const i = indexOfId(keys, key);
  if (i === 0) tx.fail(reason(MOTION_MSG.firstKeyJump));
  if (info.prop === 'gif' && i === keys.length - 1) tx.fail(reason(MOTION_MSG.gifTrailingJump));
  const rec = keys[i] as KeyRec;
  const prev = keys[i - 1] as KeyRec;
  rec.vIn = prev.ease.kind === 'hold' ? prev.vOut : a;
  rec.vOut = b;
  commitKeys(tx, info, keys);
  syncLinkGroups(tx, linkedNeighbours(tx, key));
  focusOn(tx, info, k.t);
};

const removeJump: KeyCommands['removeJump'] = (tx, { keys: ids, keep }) => {
  let changed = 0;
  let kept = 0;
  const touched: KeyId[] = [];
  for (const [trackId, tIds] of groupByTrack(tx, ids)) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    // Front to back (MULTI-03): a Stay before a later selected key passes the new value on.
    for (const i of selectedIndices(keys, tIds)) {
      if (i === 0) continue;
      const k = keys[i] as KeyRec;
      if (sameValue(k.vIn, k.vOut)) continue;
      const prev = keys[i - 1] as KeyRec;
      if (keep === 'in') k.vOut = k.vIn;
      else if (prev.ease.kind === 'hold') {
        kept++;
        continue;
      } else k.vIn = k.vOut;
      const next = keys[i + 1];
      if (k.ease.kind === 'hold' && next !== undefined) next.vIn = k.vOut;
      changed++;
      if (k.id !== null) touched.push(k.id);
    }
    commitKeys(tx, info, keys);
  }
  if (changed === 0 && kept > 0) tx.fail(reason(MOTION_MSG.jumpKeptAfterHold, { count: kept }));
  if (kept > 0) tx.note(reason(MOTION_MSG.jumpKeptAfterHold, { count: kept }));
  syncLinkGroups(
    tx,
    touched.flatMap((id) => linkedNeighbours(tx, id)),
  );
  focusOn(tx, null, earliestBeat(tx, touched));
};

// ---------------------------------------------------------------------------------------------------------------
// Time: move, delete
// ---------------------------------------------------------------------------------------------------------------

/**
 * Smallest gap kept between a moved key and an unmoved neighbour: the caller's `minGap` (one snap step, CR-W1-10b-4),
 * else one step of dt's own grid (1/4 for whole beats).
 */
function moveGap(dt: Beat, minGap: Beat | undefined): Beat {
  if (minGap !== undefined && Beats.lt(Beats.ZERO, minGap)) return minGap;
  return Beats.make(0, 1, dt.d === 1 ? 4 : dt.d);
}

const moveKeys: KeyCommands['moveKeys'] = (tx, { keys: ids, dt, minGap }) => {
  if (Beats.eq(dt, Beats.ZERO)) return;
  if (minGap !== undefined && !Beats.isNormal(minGap)) tx.fail({ key: 'cmd.badBeat' });
  const all = withXyPartners(tx, ids);
  const groups = groupByTrack(tx, all);
  if (groups.size === 0) tx.fail(reason(MOTION_MSG.keyMissing));
  const forward = Beats.lt(Beats.ZERO, dt);
  const gap = moveGap(dt, minGap);
  let eff = dt;
  const work: { info: TrackInfo; keys: KeyRec[]; sel: number[] }[] = [];
  for (const [trackId, tIds] of groups) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    const sel = selectedIndices(keys, tIds);
    const selSet = new Set(sel);
    const first = keys[0] as KeyRec;
    const floor = Beats.cmp(first.t, Beats.ZERO) < 0 ? first.t : Beats.ZERO;
    for (const i of sel) {
      const k = keys[i] as KeyRec;
      if (forward) {
        const next = keys[i + 1];
        if (next !== undefined && !selSet.has(i + 1)) {
          const bound = Beats.sub(Beats.sub(next.t, k.t), gap);
          eff = Beats.min(eff, Beats.max(bound, Beats.ZERO));
        }
      } else {
        const prev = keys[i - 1];
        const bound =
          prev !== undefined && !selSet.has(i - 1)
            ? Beats.add(Beats.sub(prev.t, k.t), gap)
            : prev === undefined
              ? Beats.sub(Beats.min(floor, k.t), k.t)
              : null;
        if (bound !== null) eff = Beats.max(eff, Beats.min(bound, Beats.ZERO));
      }
    }
    work.push({ info, keys, sel });
  }
  if (Beats.eq(eff, Beats.ZERO)) return;
  let focus: Beat | null = null;
  for (const { info, keys, sel } of work) {
    for (const i of sel) {
      const k = keys[i] as KeyRec;
      k.t = Beats.add(k.t, eff, tx.rounding);
      if (focus === null || Beats.lt(k.t, focus)) focus = k.t;
    }
    commitKeys(tx, info, keys);
  }
  focusOn(tx, work[0]?.info ?? null, focus);
};

const deleteKeys: KeyCommands['deleteKeys'] = (tx, { keys: ids }) => {
  const primary = new Set(ids);
  const all = withXyPartners(tx, ids);
  const groups = groupByTrack(tx, all);
  if (groups.size === 0) tx.fail(reason(MOTION_MSG.keyMissing));
  const unlinked = new Set<number>();
  const focus = earliestBeat(tx, all);
  let infoForFocus: TrackInfo | null = null;
  for (const [trackId, tIds] of groups) {
    const info = requireTrack(tx, trackId);
    infoForFocus ??= info;
    const keys = readKeys(tx, trackId);
    const sel = new Set(selectedIndices(keys, tIds));
    if (sel.size === keys.length) {
      for (const k of keys) if (k.link !== 0) unlinked.add(k.link);
      commitKeys(tx, info, []);
      continue;
    }
    if (sel.has(0)) {
      const firstId = (keys[0] as KeyRec).id as KeyId;
      if (primary.has(firstId)) tx.fail(reason(MOTION_MSG.firstKeyDelete));
      sel.delete(0);
    }
    const kept: KeyRec[] = [];
    keys.forEach((k, i) => {
      if (sel.has(i)) {
        if (k.link !== 0) unlinked.add(k.link);
        const prev = kept[kept.length - 1];
        // The earlier segment now runs to the next kept key: same curve, other end value.
        if (prev !== undefined && prev.link !== 0) {
          unlinked.add(prev.link);
          prev.link = 0;
        }
        return;
      }
      kept.push(k);
    });
    commitKeys(tx, info, kept);
  }
  cleanLinks(tx, unlinked);
  focusOn(tx, infoForFocus, focus);
};

// ---------------------------------------------------------------------------------------------------------------
// Easing, Stay, jumps
// ---------------------------------------------------------------------------------------------------------------

/** Turning a Stay into a curve glides to the next key's leave value (removes the jump the Stay made). */
function glideAfterHoldOff(keys: KeyRec[], i: number, heldValue: TrackValue): void {
  const next = keys[i + 1];
  if (next !== undefined && sameValue(next.vIn, heldValue) && !sameValue(next.vIn, next.vOut)) next.vIn = next.vOut;
}

const setEase: KeyCommands['setEase'] = (tx, { segments, ease }) => {
  const all = withXyPartners(tx, segments, true);
  let skipped = 0;
  let applied = 0;
  let firstReason: ReturnType<typeof Ease.validateFor> = null;
  const changed: KeyId[] = [];
  for (const [trackId, tIds] of groupByTrack(tx, all)) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    for (const i of selectedIndices(keys, tIds)) {
      const k = keys[i] as KeyRec;
      const next = keys[i + 1];
      if (next === undefined) {
        skipped++;
        continue;
      }
      const why = Ease.validateFor(ease, easeUseFor(tx, info, k.vOut, next.vIn));
      if (why !== null) {
        skipped++;
        firstReason ??= why;
        continue;
      }
      const wasHold = k.ease.kind === 'hold';
      k.ease = ease;
      if (wasHold && ease.kind !== 'hold') glideAfterHoldOff(keys, i, k.vOut);
      applied++;
      if (k.id !== null) changed.push(k.id);
    }
    commitKeys(tx, info, keys);
  }
  if (applied === 0) tx.fail(firstReason ?? reason(MOTION_MSG.noSegments));
  if (skipped > 0) {
    tx.note(reason(MOTION_MSG.easeSkipped, { count: skipped }));
    if (firstReason !== null) tx.note(firstReason);
  }
  syncLinkGroups(tx, changed);
  focusOn(tx, null, earliestBeat(tx, changed));
  return { skipped };
};

const toggleHold: KeyCommands['toggleHold'] = (tx, { segments }) => {
  const valid = segments.filter((id) => tx.doc.keys.has(id) && startsSegment(tx, id));
  if (valid.length === 0) tx.fail(reason(MOTION_MSG.noSegments));
  const turnOn = !valid.every((id) => tx.doc.keys.get(id)?.ease.kind === 'hold');
  const all = withXyPartners(tx, valid, true);
  const changed: KeyId[] = [];
  for (const [trackId, tIds] of groupByTrack(tx, all)) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    for (const i of selectedIndices(keys, tIds)) {
      const k = keys[i] as KeyRec;
      if (keys[i + 1] === undefined) continue;
      if (turnOn) {
        if (k.ease.kind === 'hold' && k.ease.gap !== true) continue;
        k.ease = HOLD;
      } else {
        if (k.ease.kind !== 'hold') continue;
        k.ease = LINEAR;
        glideAfterHoldOff(keys, i, k.vOut);
      }
      if (k.id !== null) changed.push(k.id);
    }
    commitKeys(tx, info, keys);
  }
  syncLinkGroups(tx, changed);
  focusOn(tx, null, earliestBeat(tx, changed));
};

// ---------------------------------------------------------------------------------------------------------------
// Splits, bake, join
// ---------------------------------------------------------------------------------------------------------------

const splitRpeLinear: KeyCommands['splitRpeLinear'] = (tx, { track, t }) => {
  const info = requireTrack(tx, track);
  const keys = readKeys(tx, track);
  const unlinked = new Set<number>();
  const i = rpeLinearSplit(keys, t, curveCtx(tx, info), unlinked);
  if (i < 0) tx.fail(reason(MOTION_MSG.splitOutside));
  commitKeys(tx, info, keys);
  const id = (keys[indexAt(keys, t)] as KeyRec).id as KeyId;
  // The other axis gets RPE's split too when the beat is inside one of its segments, else a key that keeps its
  // curve (that one can be approximate, which is noted).
  let partner: InsertedOn | null = null;
  const partnerId = xyPartnerTrack(tx, info);
  if (partnerId !== undefined) {
    const pInfo = requireTrack(tx, partnerId);
    const pKeys = readKeys(tx, partnerId);
    if (rpeLinearSplit(pKeys, t, curveCtx(tx, pInfo), unlinked) >= 0) commitKeys(tx, pInfo, pKeys);
    else partner = { info: pInfo, res: insertOn(tx, pInfo, t, undefined, unlinked) };
  } else partner = mirrorInsert(tx, info, t, unlinked);
  cleanLinks(tx, unlinked);
  notePartnerSplit(tx, partner?.res);
  focusOn(tx, info, t);
  return id;
};

/** Largest bake divisor accepted (1/1920 beat grid x 16). */
const MAX_BAKE_DIV = 1920 * 16;
/** Most linear pieces one bake may create. */
const MAX_BAKE_PIECES = 20000;

/**
 * Cut points of RPE Cut (E§4.6) for the segment [s, e] with pieces 1/div beat long. RPE takes the horizontal beat-grid
 * line g nearest the playhead (the editor's 1/h grid; div = h x density) and cuts at g + k/div when g lies inside
 * [s, e], else at s + k/div. `anchor` is that grid line: the caller snaps the playhead to its beat grid first
 * (CR-W1-10b-16). An anchor off the 1/div grid is snapped to it; for a grid line of a 1/h grid with h dividing div
 * that changes nothing. A raw playhead therefore anchors on the 1/div line nearest it, which can differ from RPE
 * when that line is inside the segment and the beat-grid line is not (or the other way round). Without an anchor
 * the cuts start at s.
 */
export function bakeCuts(s: Beat, e: Beat, div: number, anchor?: Beat): Beat[] {
  const out: Beat[] = [];
  const step = Beats.make(0, 1, div);
  if (anchor !== undefined) {
    const g = Beats.snap(anchor, div);
    if (Beats.lte(s, g) && Beats.lte(g, e)) {
      for (let c = Beats.step(Beats.floorTo(s, div), div, 1); Beats.lt(c, e); c = Beats.add(c, step)) {
        if (Beats.lt(s, c)) out.push(c);
        if (out.length > MAX_BAKE_PIECES) break;
      }
      return out;
    }
  }
  for (let c = Beats.add(s, step); Beats.lt(c, e); c = Beats.add(c, step)) {
    out.push(c);
    if (out.length > MAX_BAKE_PIECES) break;
  }
  return out;
}

/**
 * RPE Cut (E§4.6): each selected segment becomes linear pieces 1/div beat long (anchored on the beat-grid line
 * `anchor`, the playhead snapped to the editor's grid, see `bakeCuts`), values sampled from the exact curve;
 * X/Y-linked lines cut the other axis too. Baking the FIRST segment of a track whose
 * lead-in Phira extrapolates from it (an imported lead-in) would make the first piece the extrapolated one, so that
 * lead-in becomes an explicit segment from beat 0 first (motionTime.ts `explicitLeadIn`; exact for a straight first
 * segment, otherwise the change is measured and noted).
 */
const bakeSegments: KeyCommands['bakeSegments'] = (tx, { segments, div, anchor }) => {
  if (!Number.isSafeInteger(div) || div < 1 || div > MAX_BAKE_DIV) tx.fail(reason(MOTION_MSG.bakeDivInvalid));
  const all = withXyPartners(tx, segments, true);
  const unlinked = new Set<number>();
  let pieces = 0;
  let focus: Beat | null = null;
  let splits: SplitOn | null = null;
  for (const [trackId, tIds] of groupByTrack(tx, all)) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    const original = keys.map((k) => ({ ...k }));
    const ctx = curveCtx(tx, info);
    const sel = selectedIndices(keys, tIds).filter((i) => i + 1 < keys.length);
    // Back to front, so the indices still to bake stay valid (the first segment, if selected, comes last).
    for (const selected of sel.reverse()) {
      let i = selected;
      if (i === 0) {
        const lead = explicitLeadIn(keys, (keys[0] as KeyRec).t, ctx, unlinked);
        splits = collectSplit(splits, info, lead);
        // A lead-in key at beat 0 moved the segment to index 1.
        i = indexAt(keys, (original[0] as KeyRec).t);
      }
      const a = keys[i] as KeyRec;
      const b = keys[i + 1] as KeyRec;
      const cuts = bakeCuts(a.t, b.t, div, anchor);
      if (pieces + cuts.length + 1 > MAX_BAKE_PIECES) tx.fail(reason(MOTION_MSG.bakeTooMany, { max: MAX_BAKE_PIECES }));
      if (a.link !== 0) unlinked.add(a.link);
      a.link = 0;
      pieces += bakeSegmentAt(keys, i, cuts, (c) => valueAtBeat(original, c, ctx));
      if (focus === null || Beats.lt(a.t, focus)) focus = a.t;
    }
    if (sel.length > 0) commitKeys(tx, info, keys);
  }
  if (pieces === 0) tx.fail(reason(MOTION_MSG.noSegments));
  cleanLinks(tx, unlinked);
  noteWorstSplit(tx, splits);
  focusOn(tx, null, focus);
  return { pieces };
};

const JOIN_SAMPLES = 64;
const JOIN_TOLERANCE = 1e-5;

function valuesClose(a: RawValue, b: RawValue, range: number): boolean {
  if (typeof a === 'string' || typeof b === 'string') return a === b;
  if (typeof a === 'number' && typeof b === 'number') return Math.abs(a - b) <= JOIN_TOLERANCE * Math.max(range, 1e-9);
  const x = a as readonly number[];
  const y = b as readonly number[];
  return x.every((v, i) => Math.abs(v - (y[i] ?? Number.NaN)) <= JOIN_TOLERANCE * Math.max(range, 1e-9));
}

function valueRange(values: readonly TrackValue[]): number {
  let range = 0;
  for (const a of values) {
    for (const b of values) {
      if (typeof a === 'number' && typeof b === 'number') range = Math.max(range, Math.abs(a - b));
      else if (Array.isArray(a) && Array.isArray(b))
        a.forEach((v: number, i) => (range = Math.max(range, Math.abs(v - ((b[i] as number | undefined) ?? v)))));
    }
  }
  return range;
}

/** Value of segment a -> b at progress p as Phira computes it before storing (text: the displayed string). */
function sampleSeg(a: KeyRec, b: KeyRec, p: number, ctx: CurveCtx): RawValue {
  if (a.ease.kind === 'hold') return a.vOut;
  return interp(a.vOut, b.vIn, easeY(a.ease, p, ctx.info.extra), ctx.info.valueType);
}

/** The single ease that reproduces segments P -> K -> N exactly, or null. */
function joinedEase(p: KeyRec, k: KeyRec, n: KeyRec, ctx: CurveCtx): EaseValue | null {
  if (!sameValue(k.vIn, k.vOut)) return null;
  if (p.ease.kind === 'hold' && k.ease.kind === 'hold') return sameValue(k.vOut, p.vOut) ? HOLD : null;
  const candidates: EaseValue[] = [];
  const pe = p.ease;
  const ke = k.ease;
  if (pe.kind === 'tween' && ke.kind === 'tween' && pe.id === ke.id) {
    if (Ease.rpeToTweenId(pe.id) === 2) candidates.push({ kind: 'tween', id: pe.id, left: 0, right: 1 });
    if (Math.abs(pe.right - ke.left) <= 1e-9 && pe.left < ke.right) {
      candidates.push(
        pe.left === 0 && ke.right === 1
          ? { kind: 'tween', id: pe.id, left: 0, right: 1 }
          : { kind: 'tween', id: pe.id, left: pe.left, right: ke.right },
      );
    }
  }
  if (ctx.info.valueType === 'string') candidates.push(pe, ke);
  if (candidates.length === 0) return null;
  const range = valueRange([p.vOut, k.vIn, n.vIn]);
  const tp = ctx.tempo.secAt(p.t);
  const tk = ctx.tempo.secAt(k.t);
  const tn = ctx.tempo.secAt(n.t);
  const merged0: KeyRec = { ...p };
  for (const cand of candidates) {
    merged0.ease = cand;
    let ok = true;
    for (let j = 0; j <= JOIN_SAMPLES && ok; j++) {
      const sec = tp + ((tn - tp) * j) / JOIN_SAMPLES;
      const orig =
        sec < tk ? sampleSeg(p, k, (sec - tp) / (tk - tp), ctx) : sampleSeg(k, n, (sec - tk) / (tn - tk), ctx);
      const merged = sampleSeg(merged0, n, (sec - tp) / (tn - tp), ctx);
      if (!valuesClose(orig, merged, range)) ok = false;
    }
    if (ok) return cand;
  }
  return null;
}

function joinIn(keys: KeyRec[], i: number, ctx: CurveCtx, unlinked: Set<number>): boolean {
  const p = keys[i - 1];
  const k = keys[i];
  const n = keys[i + 1];
  if (p === undefined || k === undefined || n === undefined) return false;
  const merged = joinedEase(p, k, n, ctx);
  if (merged === null) return false;
  for (const x of [p, k]) if (x.link !== 0) unlinked.add(x.link);
  p.ease = merged;
  p.link = 0;
  keys.splice(i, 1);
  return true;
}

const joinAt: KeyCommands['joinAt'] = (tx, { key }) => {
  const k = tx.doc.keys.get(key) ?? tx.fail(reason(MOTION_MSG.keyMissing));
  const info = requireTrack(tx, k.track);
  const keys = readKeys(tx, k.track);
  const i = indexOfId(keys, key);
  if (i <= 0 || i >= keys.length - 1) tx.fail(reason(MOTION_MSG.joinNeedsMiddle));
  const unlinked = new Set<number>();
  if (!joinIn(keys, i, curveCtx(tx, info), unlinked)) tx.fail(reason(MOTION_MSG.joinNotExact));
  const partnerId = xyPartnerTrack(tx, info);
  const partnerKey = partnerId === undefined ? undefined : keyAtBeat(tx, partnerId, k.t);
  commitKeys(tx, info, keys);
  if (partnerId !== undefined && partnerKey !== undefined) {
    const pInfo = requireTrack(tx, partnerId);
    const pKeys = readKeys(tx, partnerId);
    const j = indexOfId(pKeys, partnerKey);
    if (j > 0 && j < pKeys.length - 1) {
      if (!joinIn(pKeys, j, curveCtx(tx, pInfo), unlinked)) tx.fail(reason(MOTION_MSG.joinPartner));
      commitKeys(tx, pInfo, pKeys);
    }
  }
  cleanLinks(tx, unlinked);
  focusOn(tx, info, k.t);
};

// ---------------------------------------------------------------------------------------------------------------
// Extrapolate, reverse
// ---------------------------------------------------------------------------------------------------------------

function extrapolateValue(e1: TrackValue, e2: TrackValue, type: TrackInfo['valueType']): TrackValue | null {
  switch (type) {
    case 'number':
      return tidy(2 * (e2 as number) - (e1 as number));
    case 'string':
      return null;
    default: {
      const a = e1 as readonly number[];
      const b = e2 as readonly number[];
      return storeValue(
        b.map((v, i) => 2 * v - (a[i] ?? v)),
        type,
      ).value;
    }
  }
}

/** A provenance gap: no RPE event (Phira holds the previous event's end there). */
function isGap(k: KeyRec): boolean {
  return k.ease.kind === 'hold' && k.ease.gap === true;
}

/**
 * Start indices [E1, E2] of the two latest EVENTS before segment i (E§4.8: "the previous two events"): provenance
 * gaps are skipped, Stay segments count (they are constant events). Null when there are fewer than two.
 */
function previousTwoEvents(keys: readonly KeyRec[], i: number): readonly [number, number] | null {
  let e2 = -1;
  for (let j = i - 1; j >= 0; j--) {
    if (isGap(keys[j] as KeyRec)) continue;
    if (e2 < 0) e2 = j;
    else return [j, e2];
  }
  return null;
}

const extrapolate: KeyCommands['extrapolate'] = (tx, { segments }) => {
  let applied = 0;
  let skipped = 0;
  const changed: KeyId[] = [];
  for (const [trackId, tIds] of groupByTrack(tx, segments)) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    let trackApplied = 0;
    // Front to back: a pass after another pass uses its result.
    for (const i of selectedIndices(keys, tIds)) {
      const k = keys[i] as KeyRec;
      const events = previousTwoEvents(keys, i);
      if (keys[i + 1] === undefined || events === null) {
        skipped++;
        continue;
      }
      const e1 = keys[events[0]] as KeyRec;
      const e2 = keys[events[1]] as KeyRec;
      // E.start is the start key's leave value, E.end the next key's arrive value.
      const start = extrapolateValue(e1.vOut, e2.vOut, info.valueType);
      const end = extrapolateValue(
        (keys[events[0] + 1] as KeyRec).vIn,
        (keys[events[1] + 1] as KeyRec).vIn,
        info.valueType,
      );
      if (start === null || end === null) {
        skipped++;
        continue;
      }
      // Writing a gap's value makes it an explicit Stay (any edit clears the provenance flag).
      if (isGap(k)) k.ease = HOLD;
      k.vOut = start;
      // The segment's new end; a last key without a jump holds it afterwards (as RPE / Phira hold the end).
      setArrive(keys, i + 1, k.ease.kind === 'hold' ? start : end);
      trackApplied++;
      if (k.id !== null) changed.push(k.id);
    }
    if (trackApplied > 0) commitKeys(tx, info, keys);
    applied += trackApplied;
  }
  if (applied === 0) tx.fail(reason(MOTION_MSG.extrapolateNeedsTwo));
  if (skipped > 0) tx.note(reason(MOTION_MSG.extrapolateSkipped, { count: skipped }));
  syncLinkGroups(tx, changed);
  focusOn(tx, null, earliestBeat(tx, changed));
};

/**
 * Reverses the selected segments of each track within their span (exact, motionTime.ts `reverseWindow`: span edges
 * inside a segment and tempo changes inside the span are split shape-preservingly first, approximations noted).
 * X/Y-linked lines reverse both axes and keep their keys on the same beats.
 */
const reverseSegments: KeyCommands['reverseSegments'] = (tx, { segments }) => {
  const all = withXyPartners(tx, segments, true);
  const unlinked = new Set<number>();
  const tempoBeats = tempoChangeBeats(tx);
  const windows = new Map<string, XyWindow>();
  let done = 0;
  let focus: Beat | null = null;
  let splits: SplitOn | null = null;
  for (const [trackId, tIds] of groupByTrack(tx, all)) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    const sel = selectedIndices(keys, tIds).filter((i) => i + 1 < keys.length);
    const lo = sel[0];
    const hi = sel[sel.length - 1];
    if (lo === undefined || hi === undefined) continue;
    const s = (keys[lo] as KeyRec).t;
    const e = (keys[hi + 1] as KeyRec).t;
    splits = collectSplit(splits, info, reverseWindow(keys, s, e, curveCtx(tx, info), unlinked, tempoBeats));
    commitKeys(tx, info, keys);
    noteXyWindow(tx, windows, info, s, e);
    done++;
    if (focus === null || Beats.lt(s, focus)) focus = s;
  }
  if (done === 0) tx.fail(reason(MOTION_MSG.noSegments));
  const aligned = alignXyWindows(tx, windows, unlinked);
  cleanLinks(tx, unlinked);
  noteWorstSplit(tx, worseSplitOn(splits, aligned.edited));
  notePartnerSplit(tx, aligned.partner);
  focusOn(tx, null, focus);
};

// ---------------------------------------------------------------------------------------------------------------
// Link groups
// ---------------------------------------------------------------------------------------------------------------

const linkSegments: KeyCommands['linkSegments'] = (tx, { segments, primary }) => {
  const valid = [...new Set(segments)].filter((id) => tx.doc.keys.has(id) && startsSegment(tx, id));
  if (!valid.includes(primary)) tx.fail(reason(MOTION_MSG.linkPrimary));
  if (valid.length < 2) tx.fail(reason(MOTION_MSG.linkNeedsTwo));
  const p = tx.doc.keys.get(primary) ?? tx.fail(reason(MOTION_MSG.keyMissing));
  const pInfo = requireTrack(tx, p.track);
  const pNextId = tx.idx.nextKey(primary);
  const pNext =
    (pNextId === undefined ? undefined : tx.doc.keys.get(pNextId)) ?? tx.fail(reason(MOTION_MSG.linkPrimary));
  const old = new Set<number>();
  const group = nextLinkId(tx.doc);
  for (const [trackId, tIds] of groupByTrack(tx, valid)) {
    const info = requireTrack(tx, trackId);
    if (info.valueType !== pInfo.valueType) tx.fail(reason(MOTION_MSG.linkValueType));
    const keys = readKeys(tx, trackId);
    for (const i of selectedIndices(keys, tIds)) {
      const k = keys[i] as KeyRec;
      if (k.link !== 0) old.add(k.link);
      k.link = group;
      if (k.id === primary) continue;
      const why = Ease.validateFor(p.ease, easeUseFor(tx, info, p.vOut, pNext.vIn));
      if (why !== null) tx.fail(why);
      k.vOut = p.vOut;
      if (i === 0) k.vIn = p.vOut;
      setArrive(keys, i + 1, pNext.vIn);
      k.ease = p.ease;
    }
    commitKeys(tx, info, keys);
  }
  cleanLinks(tx, old);
  focusOn(tx, pInfo, p.t);
  return group;
};

const unlinkSegments: KeyCommands['unlinkSegments'] = (tx, { segments }) => {
  const old = new Set<number>();
  for (const id of new Set(segments)) {
    const k = tx.doc.keys.get(id);
    if (k === undefined || k.link === 0) continue;
    old.add(k.link);
    tx.update('keys', id, { link: 0 });
  }
  cleanLinks(tx, old);
};

// ---------------------------------------------------------------------------------------------------------------
// Raw events
// ---------------------------------------------------------------------------------------------------------------

function easeValidForModel(e: EaseValue, info: TrackInfo): boolean {
  switch (e.kind) {
    case 'hold':
      return true;
    case 'tween':
      return Number.isInteger(e.id) && e.id >= 1 && e.id <= 29 && e.left >= 0 && e.left < e.right && e.right <= 1;
    case 'bezier':
      return info.prop !== 'speed' && !info.extra && e.p.every((v) => Number.isFinite(v));
  }
}

const replaceTrackKeys: KeyCommands['replaceTrackKeys'] = (tx, { track, keys: lifted }) => {
  const info = requireTrack(tx, track);
  if (lifted.length === 0) tx.fail(reason(MOTION_MSG.replaceEmpty));
  const recs: KeyRec[] = lifted.map((k: LiftedKey) => ({
    id: null,
    t: k.t,
    vIn: k.vIn,
    vOut: k.vOut,
    ease: k.ease,
    link: k.link,
    src: k.src,
  }));
  recs.forEach((k, i) => {
    const prev = recs[i - 1];
    if (prev !== undefined && !Beats.lt(prev.t, k.t)) tx.fail(reason(MOTION_MSG.replaceOrder));
    if (!isValueOf(info.valueType, k.vIn) || !isValueOf(info.valueType, k.vOut))
      tx.fail(reason(MOTION_MSG.replaceValue, { type: info.valueType }));
    if (!easeValidForModel(k.ease, info)) tx.fail(reason(MOTION_MSG.replaceEase));
  });
  const old = new Set<number>();
  for (const id of tx.idx.keysByTrack(track)) {
    const l = tx.doc.keys.get(id)?.link ?? 0;
    if (l !== 0) old.add(l);
  }
  const first = recs[0] as KeyRec;
  const leadIn =
    info.prop !== null && PROP_SPECS[info.prop].leadIn === 'extrapolate' && Beats.cmp(first.t, Beats.ZERO) > 0;
  if (leadIn && info.track.importedLeadIn !== true) tx.update('tracks', track, { importedLeadIn: true });
  commitKeys(tx, info, recs, { leadIn: false });
  // X/Y link: the other position axis (when it exists) and the replaced one get the beats each one lacks.
  const unlinked = new Set<number>();
  if (info.line !== null && info.prop !== null && xyPartnerTrack(tx, info) !== undefined) {
    const aligned = alignXyAll(tx, info.line.id, info.layer, [info.prop], unlinked);
    noteWorstSplit(tx, aligned.edited);
    notePartnerSplit(tx, aligned.partner);
  }
  cleanLinks(tx, [...old, ...unlinked]);
  focusOn(tx, info, first.t);
};

// ---------------------------------------------------------------------------------------------------------------

export const keyCommands: KeyCommands = {
  ensureTrack,
  insertKey,
  insertKeysAt,
  setKeyValues,
  exprKeys,
  moveKeys,
  deleteKeys,
  setEase,
  toggleHold,
  makeJump,
  removeJump,
  splitRpeLinear,
  bakeSegments,
  joinAt,
  extrapolate,
  reverseSegments,
  linkSegments,
  unlinkSegments,
  resetProperty: (tx, { line, prop, layer }) => {
    resetPropertyBody(tx, line, prop, layer);
  },
  addLayer: (tx, { line, prop }) => addLayerBody(tx, line, prop),
  deleteLayer: (tx, { line, prop, layer }) => {
    deleteLayerBody(tx, line, prop, layer);
  },
  moveSegmentsToLayer: (tx, { segments, layer }) => {
    moveSegmentsToLayerBody(tx, segments, layer);
  },
  replaceTrackKeys,
  holdLeadIn: (tx, { track }) => {
    holdLeadInBody(tx, track);
  },
};

/** Helpers shared with the other motion commands. */
export { insertOn, mirrorInsert };
